import { stopLinkBeingFollowed } from "./maps";
import {
    RESTRICTED_AIRSPACE_STYLE,
    AAZ_STYLE,
    CRANE_6KM_ZONES_STYLE,
    CTR_STYLE,
    CUSTOM_MAP_LAYER_STYLE,
    LAND_BOUNDARIES_STYLE,
    NOTAM_STYLE
} from './style_defaults.js';
import { resolveFeatureStyle } from './feature_style.js';

//////////////////////////////////////////////////////////////////
// CENTRALIZED LAYER MANAGEMENT SYSTEM
//////////////////////////////////////////////////////////////////
// There are sevearl types of layers:
// - GeoJSON data layers (restricted airspace, land boundaries) - use google.maps.Data and support styling and click events
// - KML layers (safeguarding surfaces) - use google.maps.KmlLayer, limited styling, no click events
// - AAZ polygon layers (drone and crane zones) - custom drawn Polygons from DOM targets, support styling and click events

export const layerManager = {
    // Layer definitions for all overlay layers
    layerDefinitions: {
        'restricted-airspace': {
            type: 'geojson_data_layer',
            source: () => {
                const url = layerManager.getStaticMapLayerUrl('restricted-airspace');
                if (!url) return Promise.reject(new Error('Restricted airspace URL not configured'));
                return fetch(url).then(r => r.json());
            },
            style: RESTRICTED_AIRSPACE_STYLE
        },
        'crane-6km-zones': {
            type: 'geojson_data_layer',
            source: () => {
                const url = layerManager.getStaticMapLayerUrl('crane-6km-zones');
                if (!url) return Promise.reject(new Error('Crane 6km zones URL not configured'));
                return fetch(url).then(r => r.json());
            },
            style: CRANE_6KM_ZONES_STYLE
        },
        'land-boundaries': {
            type: 'geojson_data_layer',
            source: () => {
                const url = layerManager.getStaticMapLayerUrl('land-boundaries');
                if (!url) return Promise.reject(new Error('Land boundaries URL not configured'));
                return fetch(url).then(r => r.json());
            },
            style: LAND_BOUNDARIES_STYLE
        },
        'notams-aerodrome-obstacle': {
            type: 'viewport_geojson_data_layer',
            source: () => layerManager.getLayerConfig('notams-aerodrome-obstacle')?.url,
            style: NOTAM_STYLE
        },
        'notams-all': {
            type: 'viewport_geojson_data_layer',
            source: () => layerManager.getLayerConfig('notams-all')?.url,
            style: NOTAM_STYLE
        },
        'aaz_drone': {
            type: 'aaz_polygons',
            source: 'droneAazTargets', // From DOM targets
            style: AAZ_STYLE
        },
        'aaz_crane': {
            type: 'aaz_polygons',
            source: 'craneAazTargets', // From DOM targets
            style: AAZ_STYLE
        },
        // Note: CTR and other custom-map-layers are now dynamically registered in createCustomMapLayers()
    },

    // Designed for a single map on the page only
    map: null,
    stimulusController: null,
    layers: new Map(), // layerId -> layerObject
    loadingPromises: new Map(), // layerId -> Promise (cache loading promises)
    layerStates: new Map(), // layerId -> boolean (visibility states)
    clickableShapes: [], // Array of {shape, featureInfo, layerId} for hit testing
    geojsonDataLayers: {}, // Store GeoJSON data for hit-testing (keyed by layerId)
    isDrawingModeActive: false, // Flag to disable info panel during drawing

    // Register the single map instance
    registerMap(map, stimulusController) {
        // Tear down any in-flight viewport layers from previous map (Turbo navigation)
        for (const [layerId, layer] of this.layers) {
            if (layer?.type === 'viewport_data_layer') {
                if (layer.idleListener) google.maps.event.removeListener(layer.idleListener);
                if (layer.debounceTimer) clearTimeout(layer.debounceTimer);
                if (layer.abortController) layer.abortController.abort();
            }
        }
        this.map = map;
        this.stimulusController = stimulusController;
        this.layers.clear();
        this.layerStates.clear();
        this.clickableShapes = [];
        this.geojsonDataLayers = {};
        this.isDrawingModeActive = false;
    },

    // Set drawing mode flag (disables info panel clicks while drawing)
    setDrawingMode(active) {
        this.isDrawingModeActive = active;
    },

    // Core loading method with caching and lazy loading
    async loadLayer(layerId) {
        // Check if already loaded (e.g., custom map layers)
        if (this.layers.has(layerId)) {
            return this.layers.get(layerId);
        }

        const definition = this.layerDefinitions[layerId];
        if (!definition) {
            // For custom map layers (custom-map-layer-*), they should already be loaded
            if (layerId.startsWith('custom-map-layer-')) {
                return null;
            }
            throw new Error(`Layer '${layerId}' not defined`);
        }

        // Use cached loading promise if already in progress
        if (this.loadingPromises.has(layerId)) {
            return this.loadingPromises.get(layerId);
        }

        // Create loading promise
        const loadingPromise = this.createLayer(layerId, definition);
        this.loadingPromises.set(layerId, loadingPromise);

        try {
            const layer = await loadingPromise;
            this.layers.set(layerId, layer);
            this.loadingPromises.delete(layerId);
            return layer;
        } catch (error) {
            this.loadingPromises.delete(layerId);
            throw error;
        }
    },

    // Create layer based on type
    async createLayer(layerId, definition) {
        let layer;

        switch (definition.type) {
            case 'geojson_data_layer':
                // Create a separate Data layer instance for each geojson layer
                // This allows independent styling and visibility control
                const dataLayer = new google.maps.Data();
                const geoData = typeof definition.source === 'function'
                    ? await definition.source()
                    : definition.source;
                dataLayer.addGeoJson(geoData);

                // Apply the layer's style
                dataLayer.setStyle(definition.style);

                // Store GeoJSON for hit-testing and add click listener for all geojson_data_layer types
                this.geojsonDataLayers[layerId] = geoData;
                dataLayer.addListener('click', (event) => {
                    this.handleShapeClick(event.latLng);
                });

                // Don't add to map initially - will be shown via showLayer()
                layer = { type: 'data_layer', dataLayer: dataLayer, map: this.map };
                break;

            case 'viewport_geojson_data_layer': {
                const viewportDataLayer = new google.maps.Data();
                viewportDataLayer.setStyle(definition.style);
                this.geojsonDataLayers[layerId] = { type: 'FeatureCollection', features: [] };
                viewportDataLayer.addListener('click', (event) => {
                    this.handleShapeClick(event.latLng);
                });
                layer = {
                    type: 'viewport_data_layer',
                    dataLayer: viewportDataLayer,
                    map: this.map,
                    idleListener: null,
                    debounceTimer: null,
                    abortController: null,
                    sourceUrl: typeof definition.source === 'function' ? definition.source() : definition.source
                };
                break;
            }

            case 'kml_layer':
                // Like safeguarding surfaces - create but don't add to map initially
                layer = new google.maps.KmlLayer(definition.source, {
                    ...definition.options,
                    map: null // Don't add to map initially
                });
                // Store the map reference for later use
                layer._mapReference = this.map;
                break;

            case 'aaz_polygons':
                // Like AAZ drone or crane zones from DOM targets
                const targetName = definition.source;
                const targets = this.getTargetsFromController(targetName);
                // Determine aazType from layerId (aaz_drone or aaz_crane)
                const aazType = layerId === 'aaz_crane' ? 'crane' : 'drone';
                layer = targets ? this.drawAAZPolygons(this.map, null, targets, aazType) : [];
                break;

            case 'custom_map_layers':
                // Custom map layers from assessment templates
                layer = this.createCustomMapLayers(this.map, definition);
                break;
        }

        return layer;
    },

    // Helper to get targets from stimulus controller
    getTargetsFromController(targetName) {
        if (targetName === 'droneAazTargets' && this.stimulusController.droneAazTargets) {
            return this.stimulusController.droneAazTargets;
        }
        if (targetName === 'craneAazTargets' && this.stimulusController.craneAazTargets) {
            return this.stimulusController.craneAazTargets;
        }
        return null;
    },

    // Get URL for a static map layer from DOM metadata
    getStaticMapLayerUrl(layerKey) {
        const element = document.querySelector('[data-static-map-layers-target="layersData"]');
        if (!element) return null;
        try {
            const layers = JSON.parse(element.innerHTML);
            const layer = layers.find(l => l.layer_key === layerKey);
            return layer?.geojson_url || null;
        } catch {
            return null;
        }
    },

    // Get parsed layer config from a checkbox's data-layer-config attribute
    getLayerConfig(layerId) {
        const checkbox = document.querySelector(`input[data-layer="${layerId}"]`);
        if (!checkbox?.dataset.layerConfig) return null;
        try {
            return JSON.parse(checkbox.dataset.layerConfig);
        } catch {
            return null;
        }
    },


    // Create CTR layer (simplified - actual implementation may vary)
    // CTR layer methods removed - CTR is now loaded via custom_map_layers system

    // Create custom map layers from assessment templates
    async createCustomMapLayers(map, definition) {
        try {
            // Get custom map layers data from the DOM element
            const customMapLayersElement = document.querySelector('[data-custom-map-layers-target="layersData"]');
            if (!customMapLayersElement || !customMapLayersElement.innerHTML.trim()) {
                return;
            }

            const customMapLayersData = JSON.parse(customMapLayersElement.innerHTML);

            if (!Array.isArray(customMapLayersData) || customMapLayersData.length === 0) {
                return;
            }

            // Process each custom map layer separately, collecting promises for async loads
            const loadPromises = [];

            customMapLayersData.forEach(layerData => {
                const layerId = layerData.layer_id;

                // Read initial visibility from DOM BEFORE creating shapes
                const toggleElement = document.querySelector(`[data-layer="${layerId}"]`);
                const initiallyVisible = toggleElement?.dataset.visible === 'true';

                // Priority 1: Use inline GeoJSON if present
                if (layerData.geojson) {
                    this.loadCustomLayerInline(map, layerId, layerData, initiallyVisible);
                }
                // Priority 2: Load from file URL (async - collect promise)
                else if (layerData.geojson_url) {
                    loadPromises.push(
                        this.loadCustomLayerFromUrl(map, layerId, layerData, initiallyVisible)
                    );
                }
            });

            // Wait for all async loads to complete
            if (loadPromises.length > 0) {
                await Promise.all(loadPromises);
            }
        } catch (error) {
            // Silently handle errors
        }
    },

    // Fetch viewport-scoped GeoJSON for a layer and replace its features.
    // Cancels any in-flight request for the same layer. Dispatches a
    // 'viewport-layer-error' event when the response signals an error.
    async refreshViewportLayer(layerId) {
        const layer = this.layers.get(layerId);
        if (!layer || layer.type !== 'viewport_data_layer') return;
        if (!this.map?.getBounds()) return;

        const url = layer.sourceUrl;
        if (!url) return;

        const bounds = this.map.getBounds();
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const params = new URLSearchParams({
            min_lat: sw.lat(),
            max_lat: ne.lat(),
            min_lng: sw.lng(),
            max_lng: ne.lng()
        });

        if (layer.abortController) layer.abortController.abort();
        layer.abortController = new AbortController();

        try {
            const separator = url.includes('?') ? '&' : '?';
            const response = await fetch(`${url}${separator}${params}`, { signal: layer.abortController.signal });
            if (!response.ok) {
                console.warn(`Viewport layer ${layerId} fetch returned ${response.status}`);
                return;
            }

            const body = await response.json();
            this.clearViewportFeatures(layer, layerId);

            if (body.error === 'too_many_features') {
                this.dispatchViewportLayerEvent(layerId, { error: 'too_many_features', count: body.count, limit: body.limit });
                return;
            }

            if (body.type === 'FeatureCollection' && Array.isArray(body.features)) {
                layer.dataLayer.addGeoJson(body);
                this.geojsonDataLayers[layerId] = body;
                this.dispatchViewportLayerEvent(layerId, { error: null });
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.warn(`Failed to refresh viewport layer ${layerId}:`, error);
            }
        }
    },

    clearViewportFeatures(layer, layerId) {
        layer.dataLayer.forEach(feature => layer.dataLayer.remove(feature));
        this.geojsonDataLayers[layerId] = { type: 'FeatureCollection', features: [] };
    },

    // Toggle the inline message for any layer and dispatch the event so other
    // listeners can react. Convention: views render
    //   <small class="d-none" data-layer-message="<layerId>">...</small>
    // next to the layer toggle; this method shows it on error and hides it otherwise.
    dispatchViewportLayerEvent(layerId, detail) {
        const messageElement = document.querySelector(`[data-layer-message="${layerId}"]`);
        if (messageElement) {
            messageElement.classList.toggle('d-none', detail.error == null);
        }
        document.dispatchEvent(new CustomEvent('viewport-layer-error', {
            detail: { layerId, ...detail },
            bubbles: true
        }));
    },

    // Load GeoJSON from URL (file in public/custom_map_layers/)
    async loadCustomLayerFromUrl(map, layerId, layerData, initiallyVisible) {
        try {
            const response = await fetch(layerData.geojson_url);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const geojson = await response.json();

            // Create shapes using the loaded GeoJSON
            const shapes = this.createShapesFromGeoJson(map, geojson, layerData);

            // Set correct initial visibility on shapes
            shapes.forEach(shape => shape.setVisible(initiallyVisible));

            // Store layer and state together
            this.layers.set(layerId, shapes);
            this.layerStates.set(layerId, initiallyVisible);
        } catch (error) {
            // Silently handle errors
        }
    },

    // Load GeoJSON from inline data
    loadCustomLayerInline(map, layerId, layerData, initiallyVisible) {
        const shapes = this.createShapesFromGeoJson(map, layerData.geojson, layerData);

        // Set correct initial visibility on shapes
        shapes.forEach(shape => shape.setVisible(initiallyVisible));

        // Store layer and state together
        this.layers.set(layerId, shapes);
        this.layerStates.set(layerId, initiallyVisible);
    },

    // Create shapes from GeoJSON (shared by both approaches)
    createShapesFromGeoJson(map, geojson, layerData) {
        const shapes = [];
        const layerName = layerData.checkbox_label;

        if (!geojson || !geojson.features || !Array.isArray(geojson.features)) {
            console.warn(`Invalid GeoJSON for layer ${layerName}`);
            return shapes;
        }

        // Process each feature in the GeoJSON
        geojson.features.forEach((feature) => {
            const geometry = feature.geometry;
            const properties = feature.properties || {};
            const { shapeStyle, metadata } = resolveFeatureStyle(layerData, properties);

            // Add layer name and override source type for layer manager context
            const featureInfo = { ...metadata, layerName: layerName, sourceType: 'local' };

            if (geometry.type === 'Polygon') {
                const polygon = this.createCustomPolygon(map, geometry.coordinates, shapeStyle, featureInfo, layerData.layer_id);
                if (polygon) shapes.push(polygon);
            } else if (geometry.type === 'MultiPolygon') {
                geometry.coordinates.forEach(polygonCoords => {
                    const polygon = this.createCustomPolygon(map, polygonCoords, shapeStyle, featureInfo, layerData.layer_id);
                    if (polygon) shapes.push(polygon);
                });
            }
        });

        return shapes;
    },

    createCustomPolygon(map, coordinates, shapeStyle, featureInfo, layerId) {
        try {
            // Convert all rings (outer boundary + holes) from [lng, lat] to {lat, lng}
            const paths = coordinates.map(ring =>
                ring.map(coord => ({
                    lat: parseFloat(coord[1]),
                    lng: parseFloat(coord[0])
                }))
            );

            const polygon = new google.maps.Polygon({
                paths: paths,
                map: map,
                strokeColor: shapeStyle.strokeColor,
                fillColor: shapeStyle.fillColor,
                fillOpacity: shapeStyle.fillOpacity,
                strokeWeight: shapeStyle.strokeWeight,
                strokeOpacity: shapeStyle.strokeOpacity
            });

            // Register shape for hit testing and add click listener
            this.registerClickableShape(polygon, 'polygon', featureInfo, layerId);

            return polygon;
        } catch (error) {
            return null;
        }
    },


    /**
     * Register a shape for hit testing and add click listener
     */
    registerClickableShape(shape, shapeType, featureInfo, layerId) {
        if (!featureInfo.featureName && !featureInfo.description) {
            console.log('[LayerManager] Skipping registration - no feature info:', featureInfo.layerName);
            return;
        }

        // Store shape with its metadata for hit testing
        this.clickableShapes.push({ shape, shapeType, featureInfo, layerId });

        console.log('[LayerManager] Registered clickable shape for:', featureInfo.layerName, featureInfo.featureName || '(no name)');

        // Add click listener that checks ALL shapes at click point
        shape.addListener('click', (event) => {
            this.handleShapeClick(event.latLng);
        });
    },

    /**
     * Register a circle for hit testing and add click listener
     * Circles need special handling as they use center/radius instead of paths
     */
    registerClickableCircle(circle, featureInfo, layerId) {
        if (!featureInfo.featureName && !featureInfo.description) {
            console.log('[LayerManager] Skipping circle registration - no feature info:', featureInfo.layerName);
            return;
        }

        // Store circle with its metadata for hit testing
        this.clickableShapes.push({ shape: circle, shapeType: 'circle', featureInfo, layerId });

        console.log('[LayerManager] Registered clickable circle for:', featureInfo.layerName, featureInfo.featureName || '(no name)');

        // Add click listener that checks ALL shapes at click point
        circle.addListener('click', (event) => {
            this.handleShapeClick(event.latLng);
        });
    },

    /**
     * Check if a point is inside a circle
     */
    pointInCircle(latLng, circle) {
        const center = circle.getCenter();
        const radius = circle.getRadius();
        const distance = google.maps.geometry.spherical.computeDistanceBetween(latLng, center);
        return distance <= radius;
    },

    /**
     * Handle click on any shape - find ALL shapes at click point and dispatch single event
     */
    handleShapeClick(latLng) {
        // Skip if user is in drawing mode
        if (this.isDrawingModeActive) return;

        const featuresAtPoint = [];
        const lat = latLng.lat();
        const lng = latLng.lng();

        // Check all registered custom layer shapes
        this.clickableShapes.forEach(({ shape, shapeType, featureInfo, layerId }) => {
            if (!this.isLayerVisible(layerId)) return;

            let containsPoint = false;
            if (shapeType === 'polygon') {
                containsPoint = google.maps.geometry.poly.containsLocation(latLng, shape);
            } else if (shapeType === 'circle') {
                containsPoint = this.pointInCircle(latLng, shape);
            }

            if (containsPoint) {
                featuresAtPoint.push(featureInfo);
            }
        });

        // Check all GeoJSON data layers (if visible and data loaded)
        for (const [layerId, geoData] of Object.entries(this.geojsonDataLayers)) {
            if (this.isLayerVisible(layerId) && geoData) {
                const features = this.findGeoJsonFeaturesAtPoint(layerId, geoData, lat, lng);
                featuresAtPoint.push(...features);
            }
        }

        if (featuresAtPoint.length > 0) {
            // Deduplicate features that appear in multiple layers (e.g. same NOTAM
            // in "Below 2,000ft" and "All altitudes"). Features without a featureId
            // (custom layers, AAZ zones) are always kept.
            const seenIds = new Set();
            const uniqueFeatures = featuresAtPoint.filter(f => {
                if (!f.featureId) return true;
                if (seenIds.has(f.featureId)) return false;
                seenIds.add(f.featureId);
                return true;
            });

            const customEvent = new CustomEvent('map-features-clicked', {
                detail: {
                    features: uniqueFeatures,
                    position: { lat, lng }
                },
                bubbles: true
            });
            document.dispatchEvent(customEvent);
        }
    },

    /**
     * Get layer display info for GeoJSON data layers
     */
    getGeoJsonLayerInfo(layerId) {
        const layerInfo = {
            'restricted-airspace': {
                layerName: 'Restricted Airspace',
                defaultFeatureName: 'Restricted Zone',
                style: RESTRICTED_AIRSPACE_STYLE,
                sourceType: 'aip'
            },
            'crane-6km-zones': {
                layerName: '6km Notification Zone',
                defaultFeatureName: '6km Zone',
                style: CRANE_6KM_ZONES_STYLE,
                sourceType: 'local'
            },
            'land-boundaries': {
                layerName: 'Land Boundary',
                defaultFeatureName: 'Land Boundary',
                style: LAND_BOUNDARIES_STYLE,
                sourceType: 'local'
            },
            'notams-aerodrome-obstacle': {
                layerName: 'NOTAM',
                defaultFeatureName: 'NOTAM',
                style: NOTAM_STYLE,
                sourceType: 'notam'
            },
            'notams-all': {
                layerName: 'NOTAM',
                defaultFeatureName: 'NOTAM',
                style: NOTAM_STYLE,
                sourceType: 'notam'
            }
        };
        return layerInfo[layerId] || {
            layerName: layerId,
            defaultFeatureName: 'Feature',
            style: CUSTOM_MAP_LAYER_STYLE,
            sourceType: 'local'
        };
    },

    /**
     * Find GeoJSON features containing a point for any geojson_data_layer
     */
    findGeoJsonFeaturesAtPoint(layerId, geoData, lat, lng) {
        const features = [];
        if (!geoData?.features) return features;

        const layerInfo = this.getGeoJsonLayerInfo(layerId);

        geoData.features.forEach(feature => {
            if (feature.geometry?.type === 'Polygon') {
                const coords = feature.geometry.coordinates[0];
                if (this.pointInPolygon(lat, lng, coords)) {
                    features.push({
                        featureId: feature.properties?.id || null,
                        layerName: layerInfo.layerName,
                        featureName: feature.properties?.name || layerInfo.defaultFeatureName,
                        description: feature.properties?.description || null,
                        strokeColor: layerInfo.style.strokeColor,
                        strokeWeight: layerInfo.style.strokeWeight,
                        fillColor: layerInfo.style.fillColor,
                        fillOpacity: layerInfo.style.fillOpacity,
                        sourceType: layerInfo.sourceType
                    });
                }
            }
        });

        return features;
    },

    /**
     * Simple point-in-polygon using ray casting algorithm
     * coords is array of [lng, lat] (GeoJSON format)
     */
    pointInPolygon(lat, lng, coords) {
        let inside = false;
        for (let i = 0, j = coords.length - 1; i < coords.length; j = i++) {
            const xi = coords[i][0], yi = coords[i][1];
            const xj = coords[j][0], yj = coords[j][1];

            if (((yi > lat) !== (yj > lat)) &&
                (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi)) {
                inside = !inside;
            }
        }
        return inside;
    },

    // Show layer method
    async showLayer(layerId) {
        const layer = await this.loadLayer(layerId);

        // Handle custom map layers (they don't have definitions)
        if (layerId.startsWith('custom-map-layer-')) {
            if (Array.isArray(layer)) {
                layer.forEach((shape) => {
                    shape.setVisible(true);
                });
            }
            this.layerStates.set(layerId, true);
            this.updateAllToggleUI(layerId, true);
            return;
        }

        const definition = this.layerDefinitions[layerId];
        if (!definition) {
            console.warn(`No definition found for layer ${layerId}`);
            return;
        }

        switch (definition.type) {
            case 'geojson_data_layer':
                // Add the Data layer to the map to show it
                layer.dataLayer.setMap(layer.map);
                break;

            case 'viewport_geojson_data_layer':
                if (layer.idleListener) {
                    google.maps.event.removeListener(layer.idleListener);
                    layer.idleListener = null;
                }
                if (layer.debounceTimer) {
                    clearTimeout(layer.debounceTimer);
                    layer.debounceTimer = null;
                }
                if (layer.abortController) {
                    layer.abortController.abort();
                    layer.abortController = null;
                }
                layer.dataLayer.setMap(layer.map);
                await this.refreshViewportLayer(layerId);
                layer.idleListener = layer.map.addListener('idle', () => {
                    if (layer.debounceTimer) clearTimeout(layer.debounceTimer);
                    layer.debounceTimer = setTimeout(() => {
                        if (this.isLayerVisible(layerId)) this.refreshViewportLayer(layerId);
                    }, 400);
                });
                break;

            case 'kml_layer':
                // KML layers - add to map to show
                if (!layer.getMap()) {
                    layer.setMap(layer._mapReference);
                }
                break;

            case 'aaz_polygons':
            case 'crane_6km_circles':
            case 'ctr_polygons':
            case 'custom_map_layers':
                // Arrays of shapes
                if (Array.isArray(layer)) {
                    layer.forEach((shape) => {
                        shape.setVisible(true);
                    });
                } else if (layer && layer.setVisible) {
                    layer.setVisible(true);
                }
                break;
        }

        this.layerStates.set(layerId, true);
        // Update all UI elements when layer state changes
        this.updateAllToggleUI(layerId, true);
    },

    // Hide layer method
    async hideLayer(layerId) {
        const layer = this.layers.get(layerId);
        if (!layer) return;

        // Handle custom map layers (they don't have definitions)
        if (layerId.startsWith('custom-map-layer-')) {
            if (Array.isArray(layer)) {
                layer.forEach(shape => shape.setVisible(false));
            }
            this.layerStates.set(layerId, false);
            this.updateAllToggleUI(layerId, false);
            return;
        }

        const definition = this.layerDefinitions[layerId];
        if (!definition) {
            console.warn(`No definition found for layer ${layerId}`);
            return;
        }

        switch (definition.type) {
            case 'geojson_data_layer':
                // Remove the Data layer from the map to hide it
                layer.dataLayer.setMap(null);
                break;

            case 'viewport_geojson_data_layer':
                if (layer.idleListener) {
                    google.maps.event.removeListener(layer.idleListener);
                    layer.idleListener = null;
                }
                if (layer.debounceTimer) {
                    clearTimeout(layer.debounceTimer);
                    layer.debounceTimer = null;
                }
                if (layer.abortController) {
                    layer.abortController.abort();
                    layer.abortController = null;
                }
                this.clearViewportFeatures(layer, layerId);
                layer.dataLayer.setMap(null);
                this.dispatchViewportLayerEvent(layerId, { error: null });
                break;

            case 'kml_layer':
                // KML layers - remove from map to hide
                layer.setMap(null);
                break;

            case 'aaz_polygons':
            case 'crane_6km_circles':
            case 'ctr_polygons':
            case 'custom_map_layers':
                if (Array.isArray(layer)) {
                    layer.forEach(shape => shape.setVisible(false));
                } else if (layer && layer.setVisible) {
                    layer.setVisible(false);
                }
                break;
        }

        this.layerStates.set(layerId, false);
        // Update all UI elements when layer state changes
        this.updateAllToggleUI(layerId, false);
    },

    // Toggle layer visibility
    async toggleLayer(layerId) {
        const isVisible = this.isLayerVisible(layerId);
        if (isVisible) {
            await this.hideLayer(layerId);
        } else {
            await this.showLayer(layerId);
        }
        return !isVisible; // Return new state
    },

    // Restore each custom map layer to its default visibility as declared in the DOM
    async resetDefaultLayerVisibility() {
        const toggles = document.querySelectorAll('[data-layer^="custom-map-layer-"]');
        await Promise.all(Array.from(toggles).map(toggle => {
            const layerId = toggle.dataset.layer;
            return toggle.dataset.defaultVisible === 'true'
                ? this.showLayer(layerId)
                : this.hideLayer(layerId);
        }));
    },

    // Check if layer is visible
    isLayerVisible(layerId) {
        return this.layerStates.get(layerId) || false;
    },

    // Set layer visibility (defensive method for controllers)
    async setLayerVisibility(layerId, visible) {
        if (!this.layerDefinitions[layerId] && !this.layers.has(layerId)) {
            console.warn(`Layer ${layerId} not found, skipping visibility change`);
            return;
        }

        const currentlyVisible = this.isLayerVisible(layerId);
        if (currentlyVisible !== visible) {
            await this.toggleLayer(layerId);
        }
    },

    // Reset all layer toggles (standard + custom) within a controller element to their
    // server-rendered data-default-visible defaults. Replaces the two-step pattern of
    // manually looping standard toggles then calling resetDefaultLayerVisibility().
    async resetAllLayerDefaults(controllerElement, controllerName) {
        const toggles = controllerElement.querySelectorAll(
            `[data-action*="${controllerName}#toggleLayer"]`
        );
        await Promise.all(Array.from(toggles).map(toggle => {
            const layerId = toggle.dataset.layer;
            if (!layerId) return Promise.resolve();
            const shouldBeVisible = toggle.dataset.defaultVisible === 'true';
            return this.setLayerVisibility(layerId, shouldBeVisible);
        }));
    },

    // Initialize layers on an existing map
    async initializeLayers(existingMap, stimulusController) {
        this.registerMap(existingMap, stimulusController);

        // Discover static layers from DOM, excluding custom layers
        let layersToLoad = this.discoverLayersFromDOM()
            .filter(layerId => !layerId.startsWith('custom-map-layer-'));

        // Load custom map layers AND static layers in parallel (single Promise.all)
        const staticLayerPromises = layersToLoad.map(layerId =>
            this.loadLayer(layerId).catch(error => {
                console.warn(`Failed to load layer ${layerId}:`, error);
                return null;
            })
        );
        const customLayerPromise = this.createCustomMapLayers(existingMap).catch(error => {
            console.warn('Failed to create custom map layers:', error);
        });

        await Promise.all([customLayerPromise, ...staticLayerPromises]);

        // Apply initial visibility from DOM attributes (for non-custom layers only)
        const initialVisibility = this.readInitialVisibilityFromDOM(layersToLoad);
        for (const [layerId, visible] of Object.entries(initialVisibility)) {
            if (layersToLoad.includes(layerId)) {
                try {
                    if (visible) {
                        await this.showLayer(layerId);
                    } else {
                        await this.hideLayer(layerId);
                    }
                } catch (error) {
                    console.warn(`Failed to ${visible ? 'show' : 'hide'} layer ${layerId}:`, error);
                }
            }
        }

        // Hide toggles for layers that have no data available
        this.hideUnavailableLayerToggles(layersToLoad);

    },

    // Hide toggles for layers that don't have data available
    hideUnavailableLayerToggles(layersToLoad) {
        // Check if custom-map-layers should be hidden (no custom map layers data)
        if (layersToLoad.includes('custom-map-layers')) {
            const customMapLayersElement = document.querySelector('[data-custom-map-layers-target="layersData"]');
            if (!customMapLayersElement || !customMapLayersElement.innerHTML.trim()) {
                // console.log('No custom map layers data found, hiding custom-map-layers toggle');
                this.hideLayerToggle('custom-map-layers');
            }
        }
    },

    // Hide a layer toggle from the UI
    hideLayerToggle(layerId) {
        const container = document.getElementById(`layer-${layerId}`);
        if (container) {
            container.style.display = 'none';
        }
    },

    // Discover required layers from HTML data-layer attributes
    discoverLayersFromDOM() {
        const layerElements = document.querySelectorAll('[data-layer]');
        const discoveredLayers = new Set();

        layerElements.forEach(element => {
            const layerName = element.dataset.layer;
            if (layerName && this.layerDefinitions[layerName]) {
                discoveredLayers.add(layerName);
            }
        });

        const layers = Array.from(discoveredLayers);
        // console.log('Discovered layers from DOM:', layers);
        return layers;
    },

    // Read initial visibility from data-visible attributes on toggle elements
    readInitialVisibilityFromDOM(layersToLoad) {
        const visibility = {};

        layersToLoad.forEach(layerId => {
            // Find toggle elements with data-layer attribute matching this layer
            const toggleElements = document.querySelectorAll(`[data-layer="${layerId}"]`);

            if (toggleElements.length > 0) {
                // Use the first toggle element's data-visible attribute
                const firstElement = toggleElements[0];
                const dataVisible = firstElement.dataset.visible;

                // Convert string to boolean
                visibility[layerId] = dataVisible === 'true';

                // console.log(`Layer ${layerId}: initial visibility from DOM = ${visibility[layerId]}`);
            } else {
                // Default to false if no toggle element found
                visibility[layerId] = false;
                console.log(`Layer ${layerId}: no toggle element found, defaulting to false`);
            }
        });

        return visibility;
    },


    // Draw single AAZ polygon from encoded path (simple version without click registration)
    drawAAZPolygonFromPath(map, path) {
        return new google.maps.Polygon({ paths: google.maps.geometry.encoding.decodePath(path), map: map, ...AAZ_STYLE });
    },

    // Draw single AAZ polygon with click registration for info panel
    drawAAZPolygonWithInfo(map, path, zoneName, aazType = 'drone', description = null) {
        const polygon = new google.maps.Polygon({
            paths: google.maps.geometry.encoding.decodePath(path),
            map: map,
            ...AAZ_STYLE
        });

        const layerId = aazType === 'crane' ? 'aaz_crane' : 'aaz_drone';
        const featureName = aazType === 'crane'
            ? 'Crane Auto Approval Zone'
            : 'Drone Auto Approval Zone';

        const featureInfo = {
            layerName: 'Auto Approval Zone',
            featureName: featureName,
            description: description || zoneName || '',
            strokeColor: AAZ_STYLE.strokeColor,
            strokeWeight: AAZ_STYLE.strokeWeight,
            fillColor: AAZ_STYLE.fillColor,
            fillOpacity: AAZ_STYLE.fillOpacity,
            sourceType: 'aaz'
        };

        // Register polygon for click detection
        this.registerClickableShape(polygon, 'polygon', featureInfo, layerId);

        return polygon;
    },

    // Draw AAZ polygons from targets (for dashboard page)
    // aazType should be 'drone' or 'crane' for appropriate description
    drawAAZPolygons(map, element, aazTargets, aazType = 'drone') {
        if (aazTargets) { // layerManager may pass null element
            const layerId = aazType === 'crane' ? 'aaz_crane' : 'aaz_drone';
            const featureName = aazType === 'crane'
                ? 'Crane Auto Approval Zone'
                : 'Drone Auto Approval Zone';

            let aazs = aazTargets.map((target) => {
                const polygon = this.drawAAZPolygonFromPath(map, target.dataset.polygon);
                const zoneName = target.dataset.name || '';
                const description = target.dataset.description || zoneName;

                // Create feature info for info panel
                const featureInfo = {
                    layerName: 'Auto Approval Zone',
                    featureName: featureName,
                    description: description,
                    strokeColor: AAZ_STYLE.strokeColor,
                    strokeWeight: AAZ_STYLE.strokeWeight,
                    fillColor: AAZ_STYLE.fillColor,
                    fillOpacity: AAZ_STYLE.fillOpacity,
                    sourceType: 'aaz'
                };

                // Register polygon for click detection
                this.registerClickableShape(polygon, 'polygon', featureInfo, layerId);

                return polygon;
            });
            // Start hidden - layerManager will control visibility
            aazs.forEach((aaz) => { aaz.setVisible(false); });
            return aazs;
        }
        return [];
    },

    // Unified toggle method for all controllers
    async handleToggleLayer(event) {
        stopLinkBeingFollowed(event);

        // Get layer name from data attribute
        const layerName = event.currentTarget.dataset.layer;
        if (!layerName) {
            console.error('No layer specified in data-layer attribute');
            return;
        }

        // Check current layer state
        const currentlyVisible = this.isLayerVisible(layerName);

        // For checkboxes, check if they're in sync with layer state
        // Only toggle if there's a mismatch (happens on user click, not programmatic updates)
        if (event.currentTarget.type === 'checkbox') {
            const checkboxState = event.currentTarget.checked;
            if (checkboxState === currentlyVisible) {
                // Already in sync - this is a programmatic change, don't toggle
                return;
            }
        }

        try {
            const newState = await this.toggleLayer(layerName);
            this.updateAllToggleUI(layerName, newState);
        } catch (error) {
            console.error(`Failed to toggle layer ${layerName}:`, error);
        }
    },

    // Update UI state after toggle
    updateToggleUI(element, isVisible) {
        element.dataset.visible = isVisible.toString();

        if (isVisible) {
            element.classList.add('active');
        } else {
            element.classList.remove('active');
        }
    },

    // Update ALL UI elements for a given layer (handles multiple toggle types)
    updateAllToggleUI(layerId, isVisible) {
        // Update data-layer elements (links/buttons, but NOT checkboxes)
        const dataLayerElements = document.querySelectorAll(`[data-layer="${layerId}"]`);
        dataLayerElements.forEach(element => {
            // Skip checkboxes - they're handled separately below to avoid event loops
            if (element.type !== 'checkbox') {
                this.updateToggleUI(element, isVisible);
            }
        });

        // Update checkboxes by data-layer attribute (avoids needing ID mapping)
        const checkbox = document.querySelector(`input[type="checkbox"][data-layer="${layerId}"]`);
        if (checkbox) {
            if (checkbox.checked !== isVisible) {
                checkbox.checked = isVisible;
            }
            checkbox.dataset.visible = isVisible.toString();
        }
    }
};
